import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { Capacitor } from '@capacitor/core';

import { logger } from '#lib/logger';
import { delay } from '#helpers/delay.helper';
import {
  MemoryAsset,
  MemoryAssetsResponse,
  MemoryAssetResponse,
  MemoryAssetPayloadRequest,
  MemoryAssets,
  UpdateMemoryAssetItem,
  UpdateMemoryAssetsResponse,
} from '#models/db/memory/memory-asset.model';
import { ApiRepositoryService } from '#repositories/api-repository/api-repository.service';
import { IntermediaryService } from '#services/intermediary/intermediary.service';
import { AssetRepositoryService } from '#repositories/asset-repository/asset-repository.service';
import { IMemoryAssetsService } from './memory-assets.interface';
import { BucketName } from '#models/db/asset/asset.model';
import { FileSystemService } from '#services/file-system/file-system.service';
import { AuthService } from '#services/auth/auth.service';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 20;

@Injectable({
  providedIn: 'root',
})
export class MemoryAssetsService implements IMemoryAssetsService {
  path = 'memory-assets';

  memoryAssets$ = new BehaviorSubject<MemoryAssets | null>(null);

  private activePolls = new Set<string>();

  deleteMemoryAssetsLoading$ = new Subject<boolean>();
  comprehensiveMemoryDeleteLoading$ = new Subject<boolean>();
  getMemoryAssetsLoading$ = new BehaviorSubject<boolean>(false);
  getAssetByIdLoading$ = new Subject<boolean>();

  constructor(
    private apiRepository: ApiRepositoryService,
    private assetRepository: AssetRepositoryService,
    private intermediaryHandler: IntermediaryService,
    private fileSystemService: FileSystemService,
    private authService: AuthService
  ) {}

  async createMemoryAssets(
    payload: MemoryAssetPayloadRequest[],
    nextFn?: (data: MemoryAsset[] | null) => void
  ) {
    await this.intermediaryHandler.intermediary(
      {
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.post<
          MemoryAssetsResponse,
          MemoryAssetPayloadRequest[]
        >({ url: this.path }, payload);

        logger.log(response.details, 'MemoryAssetsService.createMemoryAssets -> response');

        return response.details;
      }
    );
  }

  async getMemoryAssets(
    memoryId: string,
    nextFn?: (data: MemoryAsset[] | null) => void,
    silent = false
  ) {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getMemoryAssetsLoading$ : undefined,
        nextFn,
      },
      async () => {
        const url = `${this.path}/${memoryId}`;

        const response = await this.apiRepository.get<MemoryAssetsResponse>({
          url,
        });

        logger.log(response.details, 'MemoryAssetsService.getMemoryAssets -> response');

        const assets = response.details;
        const assetIdsToPoll: string[] = [];
        const assetsWithThumbnails: MemoryAsset[] = [];

        // Phase 1: Classify assets
        for (const asset of assets) {
          if (!asset.thumbnail_location) {
            if (asset.id) {
              assetIdsToPoll.push(asset.id);
            }
          } else {
            assetsWithThumbnails.push(asset);
          }
        }

        // Emit immediately — dismisses loading overlay fast
        this.setMemoryAssets({ assets });

        // Phase 2: Resolve signed URLs in parallel (fire-and-forget)
        this.resolveAllThumbnailUrls(assets, assetsWithThumbnails);

        // Phase 3: Poll for real thumbnails (fire-and-forget)
        for (const assetId of assetIdsToPoll) {
          this.pollForThumbnail(assetId);
        }

        return assets;
      }
    );
  }

  getMemoryAssetById(
    assetId: string,
    nextFn?: (data: MemoryAsset | null) => void,
    thumbnailOnly?: boolean
  ) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.getAssetByIdLoading$,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<MemoryAssetResponse>({
          url: `${this.path}/asset/${assetId}`,
        });

        logger.log(response.details, 'MemoryAssetsService.getMemoryAssetById -> response');

        const memoryAsset = response.details;
        const path = thumbnailOnly
          ? (memoryAsset.thumbnail_location ?? memoryAsset.storage_location)
          : memoryAsset.storage_location;
        const bucketName: BucketName =
          thumbnailOnly && memoryAsset.thumbnail_location
            ? 'memory-thumbnail-uploads'
            : 'memory-asset-uploads';

        if (path) {
          const signedUrl = await this.assetRepository.getSignedUrl(bucketName, path);
          memoryAsset.storage_signed_url = signedUrl;
        }

        return response.details;
      }
    );
  }

  async deleteMemoryAssets(asset: MemoryAsset, fileName: string): Promise<void> {
    await this.intermediaryHandler.intermediary(
      {
        loading: this.deleteMemoryAssetsLoading$,
      },
      async () => {
        this.deleteMemoryAssetsLoading$.next(true);

        const response = await this.apiRepository.delete<MemoryAssetResponse>({
          url: `${this.path}/${asset.id}`,
        });

        logger.log(response, 'MemoryAssetsService.deleteMemoryAssets -> response');

        // Remove the file from the local file system if on a native platform.
        // Only do this if the asset is not a video, as videos are handled differently.
        if (Capacitor.isNativePlatform() && !asset.mime_type?.includes('video')) {
          const bucketName: BucketName = 'memory-asset-uploads';

          await this.fileSystemService.removeFile(`${bucketName}/${fileName}`);
        }

        if (asset.id) {
          this.activePolls.delete(asset.id);
        }

        const currentAssets = this.memoryAssets$.value?.assets || [];

        this.setMemoryAssets({
          assets: currentAssets.filter((media) => media.id !== asset.id) ?? [],
        });

        return;
      }
    );
  }

  async deleteMemoryAssetsByMemoryId(memoryId: string): Promise<void> {
    await this.intermediaryHandler.intermediary(
      {
        loading: this.comprehensiveMemoryDeleteLoading$,
      },
      async () => {
        const response = await this.apiRepository.delete<MemoryAssetResponse>({
          url: `${this.path}/memory/${memoryId}`,
        });

        logger.log(response, 'MemoryAssetsService.deleteMemoryAssetsByMemoryId -> response');

        this.activePolls.clear();

        // Remove the directory from the local file system if on a native platform.
        if (Capacitor.isNativePlatform()) {
          const bucketName: BucketName = 'memory-asset-uploads';

          await this.fileSystemService.removeDirectory(
            `${bucketName}/${this.authService.getPublicUserId()}/${memoryId}`
          );
        }

        this.setMemoryAssets(null);

        return;
      }
    );
  }

  async getSignedUrl(bucketName: BucketName, path: string, nextFn?: (data: string | null) => void) {
    this.intermediaryHandler.intermediary(
      {
        nextFn,
      },
      async () => {
        const response = await this.assetRepository.getSignedUrl(bucketName, path);

        logger.log(response, 'MemoryAssetsService.getSignedUrl -> response');

        return response;
      }
    );
  }

  /**
   * Updates memory assets with new sort order and/or key photo designation.
   *
   * @param memoryId - The memory ID whose assets are being updated.
   * @param assetUpdates - Array of asset updates containing id, sort_order, and optional is_key_photo.
   */
  async updateMemoryAssets(memoryId: string, assetUpdates: UpdateMemoryAssetItem[]): Promise<void> {
    await this.intermediaryHandler.intermediary({}, async () => {
      const response = await this.apiRepository.patch<
        UpdateMemoryAssetsResponse,
        UpdateMemoryAssetItem[]
      >({ url: `${this.path}/${memoryId}` }, assetUpdates);

      logger.log(response, 'MemoryAssetsService.updateMemoryAssets -> response');

      return null;
    });
  }

  setMemoryAssets(memoryAssets: MemoryAssets | null) {
    logger.log(memoryAssets, 'MemoryAssetsService.setMemoryAssets -> memoryAssets');
    this.memoryAssets$.next(memoryAssets);
  }

  setComprehensiveMemoryDeleteLoading(loading: boolean) {
    this.comprehensiveMemoryDeleteLoading$.next(loading);
  }

  /**
   * Resets the memory assets by cleaning up any existing blob URLs and setting the memory assets state to null.
   * Logs the completion of the reset operation.
   *
   * @remarks
   * If there are currently loaded assets, their associated blob URLs are cleaned up before resetting the state.
   */
  resetMemoryAssets() {
    this.activePolls.clear();

    const currentAssets = this.memoryAssets$.value?.assets;

    if (currentAssets) {
      this.cleanupBlobUrls(currentAssets);
    }

    this.memoryAssets$.next(null);
    logger.log('complete', 'MemoryAssetsService.resetMemoryAssets');
  }

  private async resolveAllThumbnailUrls(
    allAssets: MemoryAsset[],
    assetsWithThumbnails: MemoryAsset[]
  ): Promise<void> {
    const thumbnailPromises = assetsWithThumbnails.map((asset) =>
      this.resolveAssetThumbnailUrl(asset).catch((error) => {
        logger.log(error, `resolveAllThumbnailUrls -> error for asset ${asset.id}`);
      })
    );

    await Promise.allSettled(thumbnailPromises);

    // Re-emit with resolved URLs — swaps skeletons for images
    this.setMemoryAssets({ assets: allAssets });
  }

  private async resolveAssetThumbnailUrl(asset: MemoryAsset): Promise<void> {
    const path = asset.thumbnail_location;

    if (!path) return;

    const bucketName: BucketName = 'memory-thumbnail-uploads';

    // On native platforms, check local file cache first (skip videos)
    if (Capacitor.isNativePlatform() && !asset.mime_type?.includes('video')) {
      const localFileExists = await this.fileSystemService.fileExists(`${bucketName}/${path}`);

      if (localFileExists) {
        const fileBlob = await this.fileSystemService.readFileAsBlob(localFileExists.uri);

        if (fileBlob) {
          const blobUrl = URL.createObjectURL(fileBlob);

          asset.thumbnail_signed_url = blobUrl;
          asset.local_blob_url = blobUrl;
          return;
        }
      }
    }

    // Fall back to remote signed URL
    const signedUrl = await this.assetRepository.getSignedUrl(bucketName, path);

    asset.thumbnail_signed_url = signedUrl;

    // Save to local cache in background on native
    if (Capacitor.isNativePlatform() && !asset.mime_type?.includes('video')) {
      this.fileSystemService.saveFile(signedUrl, `${bucketName}/${path}`);
    }
  }

  async pollForThumbnail(assetId: string): Promise<void> {
    if (this.activePolls.has(assetId)) return;

    this.activePolls.add(assetId);

    try {
      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
        await delay(POLL_INTERVAL_MS);

        // Stop if navigated away (activePolls cleared)
        if (!this.activePolls.has(assetId)) return;

        try {
          const response = await this.apiRepository.get<MemoryAssetResponse>({
            url: `${this.path}/asset/${assetId}`,
          });

          const asset = response.details;

          if (asset.thumbnail_location) {
            const bucketName: BucketName = 'memory-thumbnail-uploads';
            const signedUrl = await this.assetRepository.getSignedUrl(
              bucketName,
              asset.thumbnail_location
            );

            const currentAsset = this.memoryAssets$.value?.assets?.find((a) => a.id === assetId);

            if (currentAsset) {
              this.updateAssetThumbnail(assetId, signedUrl, asset.thumbnail_location);
            }

            if (Capacitor.isNativePlatform()) {
              this.fileSystemService.saveFile(
                signedUrl,
                `${bucketName}/${asset.thumbnail_location}`
              );
            }

            return;
          }
        } catch (error: unknown) {
          logger.log(error, `MemoryAssetsService.pollForThumbnail -> error (${assetId})`);

          // Asset was deleted — stop polling immediately
          if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
            return;
          }
        }
      }
    } finally {
      this.activePolls.delete(assetId);
    }
  }

  private updateAssetThumbnail(
    assetId: string,
    signedUrl: string,
    thumbnailLocation: string | null
  ): void {
    const current = this.memoryAssets$.value;

    if (!current?.assets) return;

    const updatedAssets = current.assets.map((asset) => {
      if (asset.id !== assetId) return asset;

      // Revoke old blob URL if present
      if (asset.local_blob_url) {
        URL.revokeObjectURL(asset.local_blob_url);
      }

      return {
        ...asset,
        thumbnail_signed_url: signedUrl,
        thumbnail_location: thumbnailLocation,
        local_blob_url: undefined,
      };
    });

    this.setMemoryAssets({ ...current, assets: updatedAssets });
  }

  private cleanupBlobUrls(assets: MemoryAsset[]) {
    assets.forEach((asset: MemoryAsset) => {
      if (asset.local_blob_url) {
        URL.revokeObjectURL(asset.local_blob_url);
      }
    });
  }
}
